import translations from '../translations';

const STATIC_LANGS = ['en', 'zh', 'es'];
const CACHE_PREFIX = 'qs_trans_v1_';
const CACHE_TTL = 7 * 24 * 60 * 60 * 1000;

// Combined cache key for all translated languages
const ALL_CACHE_KEY = 'qs_trans_all_v1';

const dynamicStore = {};

const readAllCache = () => {
  try {
    const raw = localStorage.getItem(ALL_CACHE_KEY);
    if (!raw) return {};
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) { localStorage.removeItem(ALL_CACHE_KEY); return {}; }
    return data;
  } catch { return {}; }
};

const writeAllCache = (data) => {
  try {
    localStorage.setItem(ALL_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {}
};

// Load existing translations from cache
const existingCache = readAllCache();
for (const lang in existingCache) {
  dynamicStore[lang] = existingCache[lang];
}

const readCache = (lang) => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + lang);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) { localStorage.removeItem(CACHE_PREFIX + lang); return null; }
    return data;
  } catch { return null; }
};

const writeCache = (lang, data) => {
  try {
    localStorage.setItem(CACHE_PREFIX + lang, JSON.stringify({ ts: Date.now(), data }));
  } catch {}
};

const batchTranslate = async (texts, targetLang) => {
  const joined = texts.join('\n||||\n');
  const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${encodeURIComponent(targetLang)}&dt=t&q=${encodeURIComponent(joined)}`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();

  const translated = data[0].map(seg => seg[0]).join('');
  return translated.split('\n||||\n');
};

const chunked = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export const loadLanguage = async (lang) => {
  if (STATIC_LANGS.includes(lang)) return;
  if (dynamicStore[lang]) return;

  const cached = readCache(lang);
  if (cached) { dynamicStore[lang] = cached; return; }

  const enKeys = Object.keys(translations.en);
  const enValues = enKeys.map(k => translations.en[k]);

  // Use larger batches (50) and fire them all in parallel for maximum speed
  const batches = chunked(enValues.map((v, i) => ({ key: enKeys[i], val: v })), 50);
  const result = {};

  const batchPromises = batches.map(async (batch) => {
    try {
      const texts = batch.map(b => b.val);
      const translated = await batchTranslate(texts, lang);
      return batch.map((b, i) => ({ key: b.key, value: translated[i] || b.val }));
    } catch {
      return batch.map(b => ({ key: b.key, value: b.val }));
    }
  });

  const batchResults = await Promise.all(batchPromises);
  batchResults.flat().forEach(({ key, value }) => { result[key] = value; });

  dynamicStore[lang] = result;
  writeCache(lang, result);
  // Also save to combined cache for faster loading
  const allCache = readAllCache();
  allCache[lang] = result;
  writeAllCache(allCache);
};

export const getDynamic = (lang) => dynamicStore[lang] || null;

export const clearDynamic = (lang) => {
  delete dynamicStore[lang];
  try { localStorage.removeItem(CACHE_PREFIX + lang); } catch {}
};
